import { useCallback } from 'react';
import { useWorkflowStore } from '@/state/workflow.state';
import { PROMPT_NODE_ID, IMPORT_NODE_DEFINITION_ID } from '@/consts/node-types.consts';
import { DEFAULT_NODE_WIDTH } from '@/consts/node-ui.consts';
import { PACK_UNPACK_EVENT_NAMES } from '@/consts/analytics.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFlowView, useGroupsManagerView } from '../Recipe/FlowContext';
import { createNewNode } from '../Recipe/useGraph';
import { getAbsolutePositionFromRelative } from './utils/nodeUtils';
import { PACK_UNPACK_GAP } from './packUnpack.consts';
import type { PackUnpackItem } from './packUnpack.consts';
import type { NodeId } from 'web';
import type { Node } from '@/types/node';

const NODE_HEIGHT = 544;
const GRID_COLUMNS = 4;

export function useUnpackNode() {
  const flowView = useFlowView();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const groupsManagerView = useGroupsManagerView();
  const { track } = useAnalytics();

  const unpackNode = useCallback(
    (nodeId: NodeId, items: PackUnpackItem[]) => {
      if (items.length <= 1) return;

      const node = flowView.getNodeNullable(nodeId);
      if (!node) return;

      const parentNode = node.parentId ? flowView.getNodeNullable(node.parentId) : undefined;
      const absolutePosition = parentNode ? getAbsolutePositionFromRelative(node.position, parentNode) : node.position;

      const startX = absolutePosition.x + DEFAULT_NODE_WIDTH + PACK_UNPACK_GAP;
      const startY = absolutePosition.y;

      const createdNodeIds: NodeId[] = [];

      for (let i = 0; i < items.length; i++) {
        const col = i % GRID_COLUMNS;
        const row = Math.floor(i / GRID_COLUMNS);
        const position = {
          x: startX + col * (DEFAULT_NODE_WIDTH + PACK_UNPACK_GAP),
          y: startY + row * (NODE_HEIGHT + PACK_UNPACK_GAP),
        };

        const item = items[i];
        let newNode: Node | undefined;

        if (item.type === 'file') {
          newNode = createNewNode(IMPORT_NODE_DEFINITION_ID, nodeTypes, position, item.file);
        } else {
          newNode = createNewNode(
            PROMPT_NODE_ID,
            nodeTypes,
            position,
            { prompt: item.text, result: { prompt: item.text } },
            undefined,
            { isClearInitialData: true },
          );
        }

        if (newNode) {
          flowView.addNewNode(newNode);
          createdNodeIds.push(newNode.id);
        }
      }

      if (createdNodeIds.length > 0) {
        const nodeName = node.data.name || 'Node';
        groupsManagerView.createGroupFromSelectedNodes(createdNodeIds);

        const firstChild = flowView.getNodeNullable(createdNodeIds[0]);
        const groupId = firstChild?.parentId;
        if (groupId) {
          flowView.updateNodeData(groupId, { name: `${nodeName} unpacked` });
        }

        track(
          PACK_UNPACK_EVENT_NAMES.UNPACK_NODE,
          { nodeType: node.type, itemCount: items.length },
          TrackTypeEnum.Product,
        );
      }
    },
    [flowView, nodeTypes, groupsManagerView, track],
  );

  return { unpackNode };
}
